import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PiCoinsLight, PiTelegramLogo } from "react-icons/pi";
import { FaWhatsapp } from "react-icons/fa6";
import { BiCalendar } from "react-icons/bi";
import { FaChevronDown, FaPen, FaRegStar, FaStar } from "react-icons/fa6";
import { FiImage } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import AlertMessage from "../../../atoms/AlertMessage";
import ManageWhatsAppTemplatesModal from "./ManageWhatsAppTemplatesModal";
import CreatePaymentRequestModal from "../../../modals/CreatePaymentRequestModal";
import WhatsAppImageUpload from "../../../atoms/WhatsAppImageUpload";
import WhatsAppMessagePreview, {
  PreviewButton,
  RawPreviewButton,
  resolvePreviewButtons,
} from "../providers/WhatsAppMessagePreview";
import ScheduleControl from "./shared/ScheduleControl";
import { useScheduleField } from "./shared/useScheduleField";
import { datetimeLocalSchema, scheduleJoi } from "./shared/scheduleDateTime";
import {
  useGetWhatsappConfig,
  useSendBulkWhatsApp,
  useSendUsersBulkWhatsApp,
  useResolveWhatsappPaths,
  invalidateUserPaymentLinks,
  useSaveWhatsappTemplateMapping,
  useUserPaymentLinks,
  useWhatsappCoverage,
  useSendWhatsApp,
  useSetWhatsappFavorite,
  useWhatsappSenderStatus,
} from "../../../../hooks/messages";
import { useGetEntityOwnerData } from "../../../../hooks/entity";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import NoTranslate from "../../../atoms/NoTranslate";
import WhatsappCreditWarning from "../home/WhatsappCreditWarning";
import { senderQualityLabel } from "../../../../utils/whatsappSender";

interface SendWhatsAppModalProps {
  show: boolean;
  onClose: () => void;
  identification: string;
  userName?: string;
  userPhone?: string;
  recipientFields?: Partial<Record<string, string>>;
  onAddCredit: () => void;
  isBulkMessage?: boolean;
  users?: Array<string>;
  recipientCount?: number;
  onResolved?: () => void;
  isWhatsappBlocked?: boolean;
}

interface WhatsAppTemplate {
  key: string;
  category: string;
  hasMedia: boolean;
  languages: string[];
  variables: Array<{ key: string; label: string }>;
  body?: Record<string, string> | null;
  samples?: Record<string, string[]> | null;
  sample_image_url?: string | null;
  buttons?: RawPreviewButton[] | null;
  url_variable_index?: number | null;
  favorite?: boolean;
  var_mapping?: Record<string, MappingEntry> | null;
  automatable?: boolean;
  approvedLanguages: string[];
}

interface WhatsAppCustomTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  body: string;
  variables_count: number;
  hasMedia: boolean;
  buttons?: RawPreviewButton[] | null;
  media_base_url: string | null;
  url_variable_index: number | null;
  body_samples?: string[] | null;
  favorite?: boolean;
  var_mapping?: Record<string, MappingEntry> | null;
  automatable?: boolean;
  status: string;
}

interface MappingEntry {
  source: "text" | "field" | "path";
  value?: string;
  root?: string;
  fallback?: string;
}

interface SendableTemplate {
  value: string;
  kind: "library" | "custom";
  category: string;
  languages: string[];
  hasMedia: boolean;
  favorite: boolean;
  varMapping: Record<string, MappingEntry>;
  automatable: boolean;
  samplesByLang?: Record<string, string[]> | null;
  sampleImageUrl?: string | null;
  rawButtons?: RawPreviewButton[] | null;
  // A payment template: its button's link is picked, never typed.
  isPayment: boolean;
  paymentLinkIndex?: number | null;
  variables: Array<{ key: string; label: string }>;
  body?: string;
  bodyByLang?: Record<string, string> | null;
  mediaBaseUrl?: string | null;
  urlVariableIndex?: number | null;
}

interface WhatsAppFormData {
  template_key: string;
  language: string;
  media_url?: string;
  isScheduled: boolean;
  scheduledDateTime?: string;
}

interface VarBinding {
  source: "field" | "path";
  value: string;
  // Which column a path reads from; absent means user_data, as before.
  root?: string;
  fallback: string;
}

const NATIVE_FIELDS = [
  "user_first_name",
  "user_last_name",
  "user_full_name",
  "user_email",
  "user_phone",
] as const;

const NATIVE_BY_LABEL: Record<string, string> = {
  "prénom du client": "user_first_name",
  "nom complet du client": "user_full_name",
};
const DEFAULT_ROOT = "user_data";
const CREATE_PAYMENT_LINK = "__create_payment_link__";

/** Library models carry `payment: true`; a merchant model carries the type. */
const hasPaymentButton = (buttons?: RawPreviewButton[] | null): boolean =>
  (buttons ?? []).some(
    (button) => button?.payment === true || button?.type === "payment"
  );

const fieldClass =
  "w-full rounded-md border border-gray-300 p-2.5 text-base md:py-2 md:text-sm text-gray-800 focus:border-blue-500 focus:ring-blue-500";

const VARIABLE_RE = /\{\{\s*(\d+)\s*\}\}/g;
const FIRST_VARIABLE_RE = /\{\{\s*1\s*\}\}/;

/** The opening the library's models share, measured on the catalogue, never guessed. */
const sharedOpeningOf = (bodies: string[]): string => {
  if (bodies.length < 2) return "";
  let prefix = bodies[0];
  for (const body of bodies.slice(1)) {
    let index = 0;
    while (
      index < prefix.length &&
      index < body.length &&
      prefix[index] === body[index]
    ) {
      index += 1;
    }
    prefix = prefix.slice(0, index);
    if (!prefix) return "";
  }
  // Cut back to a word boundary so the opening never ends mid-word.
  const lastSpace = prefix.lastIndexOf(" ");
  const opening = lastSpace > 0 ? prefix.slice(0, lastSpace + 1) : "";
  if (!FIRST_VARIABLE_RE.test(opening)) return "";
  return bodies.every((body) => body.length > opening.length) ? opening : "";
};

/** The one line that tells a template apart: its own words, with sample values in. */
const headlineOf = (
  body: string,
  samples?: string[] | null,
  opening?: string
): string => {
  const rest =
    opening && body.startsWith(opening) ? body.slice(opening.length) : "";
  const filled = (rest.trim() ? rest : body).replace(
    VARIABLE_RE,
    (marker, index) => {
      const sample = samples?.[Number(index) - 1];
      return sample && sample.trim() ? sample.trim() : marker;
    }
  );
  const line = filled.replace(/\s+/g, " ").trim();
  return line.charAt(0).toUpperCase() + line.slice(1);
};

// Families read off the catalogue keys. The catalogue is root-editable, so an
// unknown key must still show up: it falls into "others" rather than disappearing.
const GROUP_BY_KEY: Record<string, string> = {
  loyalty_reward: "relationship",
  feedback_request: "relationship",
};
const GROUP_BY_PREFIX: Record<string, string> = {
  order: "orders",
  appointment: "appointments",
  payment: "payments",
};
const GROUP_ORDER = [
  "orders",
  "appointments",
  "payments",
  "promotions",
  "relationship",
  "others",
] as const;

const groupOf = (tpl: SendableTemplate): string =>
  GROUP_BY_KEY[tpl.value] ??
  GROUP_BY_PREFIX[tpl.value.split("_")[0]] ??
  (tpl.category === "marketing" ? "promotions" : "others");

const SendWhatsAppModal: React.FC<SendWhatsAppModalProps> = ({
  show,
  onClose,
  identification,
  userName,
  userPhone,
  recipientFields,
  onAddCredit,
  isBulkMessage,
  users,
  recipientCount,
  onResolved,
  isWhatsappBlocked,
}) => {
  const { t, i18n } = useTranslation();
  const uiLanguage = (i18n.language || "").slice(0, 2).toLowerCase();
  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);
  const navigate = useNavigate();

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
    isFetching: isConfigFetching,
    refetch: refetchConfig,
  } = useGetWhatsappConfig(
    customer_identification,
    user.entity_identification,
    show
  );
  const { data: entityOwnerData } = useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    "whatsapp_credit"
  );
  const { mutate: sendWhatsApp, isLoading } = useSendWhatsApp();
  const { mutate: sendBulkWhatsApp, isLoading: isSendingBulk } =
    useSendBulkWhatsApp();
  const { mutate: sendUsersBulkWhatsApp, isLoading: isSendingUsersBulk } =
    useSendUsersBulkWhatsApp();
  const { mutate: setWhatsappFavorite } = useSetWhatsappFavorite();
  const { mutate: saveTemplateMapping, isLoading: isSavingMapping } =
    useSaveWhatsappTemplateMapping();

  const [variables, setVariables] = useState<Record<string, string>>({});
  const [bindings, setBindings] = useState<Record<string, VarBinding>>({});
  const [showManageTemplates, setShowManageTemplates] = useState(false);
  const [showCreatePaymentLink, setShowCreatePaymentLink] = useState(false);
  const [pickerMode, setPickerMode] = useState<"choose" | "fill">("choose");
  const [pickerLanguage, setPickerLanguage] = useState<string>("");
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  type PathCheck =
    | { status: "found"; value: string; root: string }
    | { status: "missing" }
    | { status: "error" };
  const [pathChecks, setPathChecks] = useState<Record<string, PathCheck>>({});
  const { mutateAsync: resolvePaths, isLoading: isCheckingPaths } =
    useResolveWhatsappPaths();
  const [pendingFavorite, setPendingFavorite] = useState<string | null>(null);

  // Read live whenever the modal is open, not only while the activation runs.
  const { data: senderStatus } = useWhatsappSenderStatus(
    customer_identification,
    user.entity_identification,
    show
  );
  // The poll is fresher than the config it then invalidates, so it wins.
  const effectiveSenderStatus =
    senderStatus?.sender_status || config?.sender_status || null;
  const senderPending = effectiveSenderStatus === "pending";
  const senderFailed = effectiveSenderStatus === "failed";
  const senderWasOnline =
    senderStatus?.was_online ?? config?.sender?.was_online ?? false;

  const openLargeEnterpriseSupport = () => {
    onClose();
    navigate("/control-panel/support?requestType=large_enterprise_request");
  };

  const openWhatsappSetupRequest = () => {
    onClose();
    navigate("/control-panel/support?requestType=whatsapp_setup");
  };

  const schema = useMemo(
    () =>
      scheduleJoi.object({
        template_key: scheduleJoi.string().required(),
        language: scheduleJoi.string().required(),
        media_url: scheduleJoi.string().allow("").optional(),
        isScheduled: scheduleJoi.boolean(),
        scheduledDateTime: scheduleJoi.when("isScheduled", {
          is: true,
          then: datetimeLocalSchema()
            .required()
            .messages({
              "string.empty": t(
                "dashboard.entities.viewEntityUser.sendWhatsApp.selectDateTime"
              ) as string,
              "any.required": t(
                "dashboard.entities.viewEntityUser.sendWhatsApp.selectDateTime"
              ) as string,
              "datetimeLocal.min": t(
                "dashboard.entities.viewEntityUser.sendWhatsApp.invalidScheduleTime"
              ) as string,
              "datetimeLocal.max": t(
                "dashboard.entities.viewEntityUser.sendWhatsApp.invalidScheduleTime"
              ) as string,
            }),
          otherwise: scheduleJoi.string().allow("").optional(),
        }),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<WhatsAppFormData>({
    resolver: joiResolver(schema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      template_key: "",
      language: "",
      media_url: "",
      isScheduled: false,
      scheduledDateTime: "",
    },
  });

  const templateKey = watch("template_key");
  const language = watch("language");
  const mediaUrl = watch("media_url");
  const isScheduled = watch("isScheduled");
  const scheduledDateTime = watch("scheduledDateTime");

  const schedule = useScheduleField({
    isOpen: show,
    armed: Boolean(isScheduled),
    value: scheduledDateTime ?? "",
    setArmed: (armed) =>
      setValue("isScheduled", armed, { shouldValidate: true }),
    setValue: (value) =>
      setValue("scheduledDateTime", value, { shouldValidate: true }),
  });

  const libraryTemplates: SendableTemplate[] = useMemo(
    () =>
      (config?.templates ?? [])
        .filter((tpl: WhatsAppTemplate) => tpl.approvedLanguages.length > 0)
        .map((tpl: WhatsAppTemplate) => ({
          value: tpl.key,
          kind: "library" as const,
          category: tpl.category,
          languages: tpl.approvedLanguages,
          hasMedia: tpl.hasMedia,
          favorite: tpl.favorite === true,
          varMapping: tpl.var_mapping ?? {},
          automatable: tpl.automatable === true,
          rawButtons: tpl.buttons ?? null,
          isPayment: hasPaymentButton(tpl.buttons),
          paymentLinkIndex: hasPaymentButton(tpl.buttons)
            ? tpl.url_variable_index ?? null
            : null,
          samplesByLang: tpl.samples,
          sampleImageUrl: tpl.sample_image_url,
          bodyByLang: tpl.body,
          variables: tpl.variables,
        })),
    [config]
  );

  const customTemplates: SendableTemplate[] = useMemo(
    () =>
      (config?.customTemplates ?? [])
        .filter((tpl: WhatsAppCustomTemplate) => tpl.status === "approved")
        .map((tpl: WhatsAppCustomTemplate) => ({
          value: tpl.id,
          kind: "custom" as const,
          category: tpl.category,
          languages: [tpl.language],
          hasMedia: tpl.hasMedia,
          favorite: tpl.favorite === true,
          varMapping: tpl.var_mapping ?? {},
          automatable: tpl.automatable === true,
          samplesByLang: tpl.body_samples
            ? { [tpl.language]: tpl.body_samples }
            : null,
          rawButtons: tpl.buttons ?? null,
          isPayment: hasPaymentButton(tpl.buttons),
          paymentLinkIndex: hasPaymentButton(tpl.buttons)
            ? tpl.url_variable_index ?? null
            : null,
          mediaBaseUrl: tpl.media_base_url,
          urlVariableIndex: hasPaymentButton(tpl.buttons)
            ? null
            : tpl.url_variable_index,
          body: tpl.body,
          variables: Array.from(
            { length: tpl.variables_count },
            (_, index) => ({
              key: String(index + 1),
              label: `{{${index + 1}}}`,
            })
          ),
        })),
    [config]
  );

  const templates: SendableTemplate[] = useMemo(
    () => [...libraryTemplates, ...customTemplates],
    [libraryTemplates, customTemplates]
  );

  const libraryLanguages = useMemo(() => {
    const languages = new Set<string>();
    libraryTemplates.forEach((tpl) =>
      tpl.languages.forEach((language) => languages.add(language))
    );
    return Array.from(languages);
  }, [libraryTemplates]);

  useEffect(() => {
    if (libraryLanguages.length === 0) return;
    setPickerLanguage((current) =>
      libraryLanguages.includes(current)
        ? current
        : libraryLanguages.includes(uiLanguage)
        ? uiLanguage
        : libraryLanguages[0]
    );
  }, [libraryLanguages, uiLanguage]);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.value === templateKey) ?? null,
    [templates, templateKey]
  );

  // A template shows in exactly one place: pinned at the top, or in its family.
  const favoriteTemplates = useMemo(() => {
    const refs: string[] = Array.isArray((config as any)?.favorites)
      ? (config as any).favorites
      : [];
    const byValue = new Map(templates.map((tpl) => [tpl.value, tpl]));
    return refs
      .map((ref) => byValue.get(ref))
      .filter((tpl): tpl is SendableTemplate => Boolean(tpl));
  }, [config, templates]);

  useEffect(() => {
    if (pickerMode === "fill" && !selectedTemplate) setPickerMode("choose");
  }, [pickerMode, selectedTemplate]);

  const ownTemplates = useMemo(
    () => customTemplates.filter((tpl) => !tpl.favorite),
    [customTemplates]
  );

  const sharedOpenings = useMemo(() => {
    const bodiesByLang: Record<string, string[]> = {};
    libraryTemplates.forEach((tpl) => {
      Object.entries(tpl.bodyByLang ?? {}).forEach(([lang, body]) => {
        if (typeof body !== "string" || !body.trim()) return;
        bodiesByLang[lang] = [...(bodiesByLang[lang] ?? []), body];
      });
    });
    return Object.fromEntries(
      Object.entries(bodiesByLang).map(([lang, bodies]) => [
        lang,
        sharedOpeningOf(bodies),
      ])
    );
  }, [libraryTemplates]);

  const groupedLibrary = useMemo(() => {
    const rest = libraryTemplates.filter((tpl) => !tpl.favorite);
    return GROUP_ORDER.map((id) => ({
      id,
      rows: rest.filter((tpl) => groupOf(tpl) === id),
    })).filter((group) => group.rows.length > 0);
  }, [libraryTemplates]);

  useEffect(() => {
    if (!show) {
      reset();
      setVariables({});
      setBindings({});
      setPickerMode("choose");
      setExpandedRows([]);
      setPathChecks({});
      setPendingFavorite(null);
      appliedTemplateKeyRef.current = null;
    }
  }, [show, reset]);

  // Key on the template id, not the memoised object: /whatsapp/config refetches rebuild the object and would wipe typed values.
  const appliedTemplateKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedTemplate) return;
    if (appliedTemplateKeyRef.current === selectedTemplate.value) return;
    appliedTemplateKeyRef.current = selectedTemplate.value;
    const preloadedVariables: Record<string, string> = {};
    const preloadedBindings: Record<string, VarBinding> = {};
    Object.entries(selectedTemplate.varMapping).forEach(([index, entry]) => {
      if (entry.source === "text") {
        preloadedVariables[index] = entry.value ?? "";
        return;
      }
      preloadedBindings[index] = {
        source: entry.source,
        value: entry.value ?? "",
        ...(entry.root ? { root: entry.root } : {}),
        fallback: entry.fallback ?? "",
      };
    });

    selectedTemplate.variables.forEach((variable) => {
      if (selectedTemplate.varMapping[variable.key]) return;
      const native = NATIVE_BY_LABEL[variable.label.trim().toLowerCase()];
      if (!native) return;
      preloadedBindings[variable.key] = {
        source: "field",
        value: native,
        fallback: t(
          "dashboard.entities.viewEntityUser.sendWhatsApp.defaultFallbackCustomer"
        ) as string,
      };
    });

    setVariables(preloadedVariables);
    setBindings(preloadedBindings);
    setExpandedRows([]);
    setPathChecks({});
    setValue("media_url", "");
  }, [selectedTemplate, setValue, t]);

  const availableCredits =
    entityOwnerData?.entity_other_data?.whatsapp_credit ??
    config?.whatsapp_credit ??
    0;

  const variableRows = useMemo(() => {
    if (!selectedTemplate) return [] as Array<{ key: string; label: string }>;
    const rows = selectedTemplate.variables.map((v) => ({
      key: v.key,
      label: v.label,
    }));
    if (selectedTemplate.urlVariableIndex != null) {
      rows.push({
        key: String(selectedTemplate.urlVariableIndex),
        label: t(
          "dashboard.entities.viewEntityUser.sendWhatsApp.buttonLinkLabel"
        ) as string,
      });
    }
    return rows;
  }, [selectedTemplate, t]);

  const checkedPath = (key: string) => pathChecks[key];
  const hasPathRow = variableRows.some(
    (row) => bindings[row.key]?.source === "path"
  );

  // Walk the remaining segments of a typed path inside one folder's content.
  const readSegments = (source: any, segments: string[]): string => {
    let current = source;
    for (const segment of segments) {
      if (current === null || current === undefined) return "";
      current = current[segment];
    }
    return typeof current === "string" ||
      typeof current === "number" ||
      typeof current === "boolean"
      ? String(current)
      : "";
  };

  const refreshPreview = async () => {
    const rows = variableRows
      .map((row) => ({ row, binding: bindings[row.key] }))
      .filter(
        ({ binding }) =>
          binding && binding.source === "path" && binding.value.trim()
      );
    if (rows.length === 0) return;

    const roots = [DEFAULT_ROOT, "user_data_preferences"];
    const fields = rows.flatMap(({ binding }) =>
      roots.map((root) => ({ root, value: binding!.value.trim() }))
    );

    let folders: Record<string, any> | null = null;
    try {
      folders = await resolvePaths({
        customer_identification,
        entity_identification: user.entity_identification,
        user_identification: identification,
        fields,
      });
    } catch (error) {
      folders = null;
    }

    const results: Record<string, PathCheck> = {};
    const settledRoots: Record<string, string> = {};
    rows.forEach(({ row, binding }) => {
      const typed = binding!.value.trim();
      if (!folders) {
        results[row.key] = { status: "error" };
        return;
      }
      const segments = typed.split(".");
      const rest = segments.slice(1);
      const reads = roots.map((root) => folders![`${root}|${segments[0]}`]);
      const found = roots
        .map((root, index) => ({
          root,
          value: readSegments(reads[index]?.value, rest),
        }))
        .find((candidate) => candidate.value);
      if (!found) {
        results[row.key] = reads.some((read) => read?.read)
          ? { status: "missing" }
          : { status: "error" };
        return;
      }
      results[row.key] = {
        status: "found",
        value: found.value,
        root: found.root,
      };
      // Guard against a row edited while the check was in flight.
      if (bindings[row.key]?.value.trim() === typed)
        settledRoots[row.key] = found.root;
    });
    setPathChecks(results);
    Object.entries(settledRoots).forEach(([key, root]) =>
      updateBinding(key, { root })
    );
  };

  const rowComplete = (key: string): boolean => {
    const b = bindings[key];
    if (b) {
      if (b.fallback.trim().length === 0) return false;
      return b.source === "path"
        ? b.value.trim().length > 0
        : (NATIVE_FIELDS as readonly string[]).includes(b.value);
    }
    return (variables[key] ?? "").trim().length > 0;
  };
  const variablesComplete = variableRows.every((r) => rowComplete(r.key));

  const savedMapping = selectedTemplate?.varMapping ?? {};
  const hasSavedMapping = Object.keys(savedMapping).length > 0;

  const currentMapping = useMemo(() => {
    const mapping: Record<string, MappingEntry> = {};
    variableRows.forEach((row) => {
      const binding = bindings[row.key];
      if (binding) {
        mapping[row.key] = {
          source: binding.source,
          value: binding.value.trim(),
          // user_data is the default on the server; only the other one travels.
          ...(binding.source === "path" &&
          binding.root &&
          binding.root !== DEFAULT_ROOT
            ? { root: binding.root }
            : {}),
          fallback: binding.fallback.trim(),
        };
        return;
      }
      const text = (variables[row.key] ?? "").trim();
      if (text) mapping[row.key] = { source: "text", value: text };
    });
    return mapping;
  }, [variableRows, bindings, variables]);

  const entryFingerprint = (entry?: MappingEntry): string =>
    entry
      ? [
          entry.source,
          entry.root ?? DEFAULT_ROOT,
          entry.value ?? "",
          entry.fallback ?? "",
        ].join("|")
      : "";
  const sameEntry = (a?: MappingEntry, b?: MappingEntry): boolean =>
    entryFingerprint(a) === entryFingerprint(b);
  const rowMatchesSaved = (key: string) =>
    sameEntry(currentMapping[key], savedMapping[key]);
  const mappingDiffers = variableRows.some((row) => !rowMatchesSaved(row.key));
  const isRowCollapsed = (key: string) =>
    Boolean(savedMapping[key]) &&
    rowMatchesSaved(key) &&
    !expandedRows.includes(key);

  // The server stores a path up to four segments deep and refuses the rest.
  const mappingSavable =
    variablesComplete &&
    mappingDiffers &&
    Object.values(currentMapping).every(
      (entry) =>
        entry.source !== "path" ||
        ((entry.value ?? "").split(".").length <= 4 &&
          (entry.value ?? "")
            .split(".")
            .every((part) => part.trim().length > 0))
    );

  // The identity the server answers with, so each figure finds its row again.
  const fieldIdentity = (entry: {
    source: string;
    value?: string;
    root?: string;
  }): string =>
    entry.source === "path"
      ? `path|${entry.root ?? DEFAULT_ROOT}|${entry.value ?? ""}`
      : `${entry.source}|${entry.value ?? ""}`;

  const coverageFields = useMemo(
    () =>
      Object.values(currentMapping)
        .filter(
          (entry) =>
            entry.source !== "text" && (entry.value ?? "").trim().length > 0
        )
        .map((entry) =>
          entry.source === "path"
            ? {
                source: "path" as const,
                root: entry.root ?? DEFAULT_ROOT,
                value: entry.value ?? "",
              }
            : { source: "field" as const, value: entry.value ?? "" }
        ),
    [currentMapping]
  );

  const coverageKey = JSON.stringify(coverageFields);
  const [settledFields, setSettledFields] = useState(coverageFields);
  const latestCoverageFields = useRef(coverageFields);
  latestCoverageFields.current = coverageFields;
  useEffect(() => {
    const timer = setTimeout(
      () => setSettledFields(latestCoverageFields.current),
      700
    );
    return () => clearTimeout(timer);
  }, [coverageKey]);

  const { data: coverageData } = useWhatsappCoverage(
    customer_identification,
    user.entity_identification,
    isBulkMessage
      ? {
          group_identification: identification,
          ...(users && users.length > 0 ? { users } : {}),
          fields: settledFields,
        }
      : { users: [identification], fields: settledFields },
    Boolean(show && isBulkMessage && pickerMode === "fill" && variablesComplete)
  );

  const coverageByIdentity = useMemo(() => {
    const byIdentity: Record<string, { present: number; checked: number }> = {};
    (coverageData?.coverage ?? []).forEach((item: any) => {
      byIdentity[fieldIdentity(item)] = {
        present: Number(item?.present ?? 0),
        checked: Number(item?.checked ?? 0),
      };
    });
    return byIdentity;
  }, [coverageData]);

  const coverageTotal: number | null =
    typeof coverageData?.total === "number" ? coverageData.total : null;
  const coverageSampled = coverageData?.sampled === true;

  const wk = (suffix: string) =>
    `dashboard.entities.viewEntityUser.sendWhatsApp.${suffix}`;

  const entryValueLabel = (entry: MappingEntry): string => {
    if (entry.source === "field")
      return t(wk(`field_${entry.value}`)) as string;
    return entry.value ?? "";
  };

  const entryOriginLabel = (entry: MappingEntry): string =>
    t(
      wk(
        entry.source === "text"
          ? "sourceFixed"
          : entry.source === "field"
          ? "sourceProfile"
          : "sourceData"
      )
    ) as string;

  // Say the certain consequence when it is known, instead of a hypothetical "if".
  const fallbackLabel = (key: string): string => {
    if (!isBulkMessage) {
      // One recipient: say whether the fallback concerns him, or only the others.
      const binding = bindings[key];
      if (!binding) return t(wk("varFallbackLabel")) as string;
      const check = pathChecks[key];
      const known =
        binding.source === "field"
          ? (recipientFields?.[binding.value] ?? "").trim().length > 0
          : check?.status === "found"
          ? true
          : check?.status === "missing"
          ? false
          : null;
      if (known === null) return t(wk("varFallbackLabel")) as string;
      return t(wk(known ? "fallbackForOthers" : "fallbackForThisClient"));
    }
    const entry = currentMapping[key];
    const figure = entry ? coverageByIdentity[fieldIdentity(entry)] : undefined;
    if (figure && figure.checked > 0 && figure.present < figure.checked) {
      const missingRate = (figure.checked - figure.present) / figure.checked;
      const count =
        coverageTotal !== null
          ? Math.round(missingRate * coverageTotal)
          : figure.checked - figure.present;
      return t(wk("fallbackForMissing"), { count }) as string;
    }
    return t(wk("varFallbackLabel")) as string;
  };

  // An estimate is announced as one; a full count is stated plainly.
  const coverageLabel = (key: string): string | null => {
    const entry = currentMapping[key];
    if (!entry || entry.source === "text") return null;
    const figure = coverageByIdentity[fieldIdentity(entry)];
    if (!figure) return null;
    if (!coverageSampled && coverageTotal !== null) {
      return t(wk("coveragePresent"), {
        count: figure.present,
        total: coverageTotal,
      }) as string;
    }
    if (coverageTotal !== null && figure.checked > 0) {
      return t(wk("coverageApprox"), {
        count: Math.round((figure.present / figure.checked) * coverageTotal),
        total: coverageTotal,
      }) as string;
    }
    return t(wk("coverageSample"), {
      count: figure.present,
      checked: figure.checked,
    }) as string;
  };

  // Who will receive the fallback instead of a real value.
  const fallbackWarnings = useMemo(() => {
    if (!isBulkMessage || !coverageData) return [];
    return variableRows
      .map((row) => {
        const entry = currentMapping[row.key];
        if (!entry || entry.source === "text") return null;
        const figure = coverageByIdentity[fieldIdentity(entry)];
        if (!figure || figure.checked === 0) return null;
        const missingRate = (figure.checked - figure.present) / figure.checked;
        if (missingRate <= 0) return null;
        const count =
          coverageTotal !== null
            ? Math.round(missingRate * coverageTotal)
            : figure.checked - figure.present;
        return {
          key: row.key,
          entry,
          fallback: entry.fallback ?? "",
          count,
          unknownToAll: entry.source === "path" && figure.present === 0,
          approximate: coverageSampled || coverageTotal === null,
        };
      })
      .filter(Boolean) as Array<{
      key: string;
      entry: MappingEntry;
      fallback: string;
      count: number;
      unknownToAll: boolean;
      approximate: boolean;
    }>;
  }, [
    isBulkMessage,
    coverageData,
    variableRows,
    currentMapping,
    coverageByIdentity,
    coverageTotal,
    coverageSampled,
  ]);

  const handleSaveMapping = () => {
    if (!selectedTemplate || !mappingSavable) return;
    saveTemplateMapping(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        data: { template: selectedTemplate.value, mapping: currentMapping },
      },
      // Saved means settled: the rows go back to their one-line summary.
      { onSuccess: () => setExpandedRows([]) }
    );
  };

  // Three mechanics, not three values: plain text, a native field, a saved field.
  type RowKind = "text" | "field" | "data";
  const rowKind = (key: string): RowKind => {
    const binding = bindings[key];
    if (!binding) return "text";
    return binding.source === "field" ? "field" : "data";
  };

  const setRowKind = (key: string, kind: RowKind) => {
    setBindings((prev) => {
      const next = { ...prev };
      const fallback = prev[key]?.fallback ?? "";
      if (kind === "text") delete next[key];
      else if (kind === "field")
        next[key] = {
          source: "field",
          value:
            prev[key]?.source === "field" ? prev[key].value : NATIVE_FIELDS[0],
          fallback,
        };
      else
        next[key] = {
          source: "path",
          value: prev[key]?.source === "path" ? prev[key].value : "",
          ...(prev[key]?.root ? { root: prev[key].root } : {}),
          fallback,
        };
      return next;
    });
  };

  // Only the native-field select calls this now: the data path is typed.
  const setRowMode = (key: string, mode: string) => {
    setBindings((prev) => {
      const next = { ...prev };
      if (mode === "text") delete next[key];
      else
        next[key] = {
          source: "field",
          value: mode,
          fallback: prev[key]?.fallback ?? "",
        };
      return next;
    });
  };

  const updatePath = (key: string, value: string) => {
    setPathChecks((prev) => ({ ...prev, [key]: undefined as never }));
    updateBinding(key, { value });
  };

  const updateBinding = (key: string, patch: Partial<VarBinding>) =>
    setBindings((prev) =>
      prev[key] ? { ...prev, [key]: { ...prev[key], ...patch } } : prev
    );

  // WhatsApp fixes the approved domain for a custom image template; only the path may vary.
  const mediaError = useMemo(() => {
    if (!selectedTemplate?.hasMedia) return null;
    const value = (mediaUrl ?? "").trim();
    if (!value) return null;
    const base = selectedTemplate.mediaBaseUrl;
    if (!base) return null;
    try {
      const parsed = new URL(value);
      const path = `${parsed.pathname.replace(/^\/+/, "")}${parsed.search}`;
      if (parsed.origin !== base || !path) {
        return t(
          "dashboard.entities.viewEntityUser.sendWhatsApp.mediaDomainError",
          { domain: base }
        ) as string;
      }
    } catch (e) {
      return t(
        "dashboard.entities.viewEntityUser.sendWhatsApp.mediaDomainError",
        { domain: base }
      ) as string;
    }
    return null;
  }, [selectedTemplate, mediaUrl, t]);

  const mediaOk =
    !selectedTemplate?.hasMedia ||
    (Boolean((mediaUrl ?? "").trim()) && !mediaError);

  // A dynamic variable has no value before the send, so its fallback stands in.
  const previewBody = useMemo(() => {
    if (!selectedTemplate) return "";
    const raw =
      selectedTemplate.kind === "custom"
        ? selectedTemplate.body ?? ""
        : selectedTemplate.bodyByLang?.[language] ?? "";
    if (!raw) return "";
    return raw.replace(/\{\{\s*(\d+)\s*\}\}/g, (marker, index) => {
      const binding = bindings[String(index)];
      if (binding) {
        const own =
          binding.source === "field" && !isBulkMessage
            ? (recipientFields?.[binding.value] ?? "").trim()
            : binding.source === "path"
            ? (() => {
                const check = pathChecks[String(index)];
                return check?.status === "found" ? check.value : "";
              })()
            : "";
        return own || binding.fallback.trim() || marker;
      }
      return (variables[String(index)] ?? "").trim() || marker;
    });
  }, [
    selectedTemplate,
    language,
    variables,
    bindings,
    recipientFields,
    isBulkMessage,
    pathChecks,
  ]);

  // The client reads the button in the message's language, not the dashboard's.
  const previewButtons: PreviewButton[] = useMemo(
    () => resolvePreviewButtons(selectedTemplate?.rawButtons, language),
    [selectedTemplate, language]
  );

  // A payment template fills its button from a link that belongs to one client.
  const paymentLinkIndex = selectedTemplate?.paymentLinkIndex ?? null;
  const isPaymentTemplate = Boolean(selectedTemplate?.isPayment);
  const { data: paymentLinks, isLoading: isLoadingPaymentLinks } =
    useUserPaymentLinks(
      customer_identification,
      user.entity_identification,
      identification,
      Boolean(show && isPaymentTemplate && !isBulkMessage)
    );

  const paymentLinkValue =
    paymentLinkIndex != null ? variables[String(paymentLinkIndex)] ?? "" : "";
  const paymentReady =
    !isPaymentTemplate ||
    (paymentLinkIndex != null && paymentLinkValue.trim().length > 0);

  const formatAmount = (amount: number, currency: string): string => {
    try {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency,
      }).format((amount ?? 0) / 100);
    } catch (error) {
      return `${((amount ?? 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const nativeExample = (field: string): string => {
    const value = isBulkMessage ? "" : (recipientFields?.[field] ?? "").trim();
    return (
      value || (t(wk(`fieldExample_${field}`), { defaultValue: "" }) as string)
    );
  };

  const isSending = isLoading || isSendingBulk || isSendingUsersBulk;

  const configured =
    config?.configured === true && effectiveSenderStatus === "active";
  const bulkRecipientCount =
    users && users.length > 0 ? users.length : recipientCount ?? 0;

  const creditsNeeded =
    isBulkMessage && bulkRecipientCount > 0 ? bulkRecipientCount : 1;
  const hasEnoughCredits = availableCredits >= creditsNeeded;

  const canSend =
    configured &&
    !isWhatsappBlocked &&
    pickerMode === "fill" &&
    paymentReady &&
    !!templateKey &&
    !!language &&
    variablesComplete &&
    mediaOk &&
    hasEnoughCredits &&
    !schedule.isIncomplete &&
    !errors.scheduledDateTime &&
    permissions.can(Cap.SEND_WHATSAPP, Level.WRITE);

  const performSend = (data: WhatsAppFormData) => {
    const scheduledDate = schedule.getScheduledDate();
    const scheduledTime = scheduledDate
      ? scheduledDate.toISOString()
      : undefined;

    const staticVariables: Record<string, string> = {};
    const variableBindings: Record<string, Record<string, unknown>> = {};
    variableRows.forEach((r) => {
      const b = bindings[r.key];
      if (b) {
        variableBindings[r.key] = {
          source: b.source,
          value: b.value.trim(),
          ...(b.source === "path" && b.root && b.root !== DEFAULT_ROOT
            ? { root: b.root }
            : {}),
          fallback: b.fallback.trim(),
        };
      } else {
        staticVariables[r.key] = (variables[r.key] ?? "").trim();
      }
    });

    if (isPaymentTemplate && paymentLinkIndex != null) {
      staticVariables[String(paymentLinkIndex)] = paymentLinkValue.trim();
    }

    const payload = {
      template_key: data.template_key,
      language: data.language,
      variables: staticVariables,
      ...(Object.keys(variableBindings).length > 0
        ? { variable_bindings: variableBindings }
        : {}),
      ...(selectedTemplate?.hasMedia && data.media_url
        ? { media_url: data.media_url }
        : {}),
      ...(scheduledTime && { schedule: scheduledTime }),
    };

    const onSuccess = () => {
      onClose();
      onResolved?.();
    };

    if (isBulkMessage) {
      if (users && users.length > 0) {
        sendUsersBulkWhatsApp(
          {
            customer_identification,
            entity_identification: user.entity_identification,
            group_identification: identification,
            data: { ...payload, users },
          },
          { onSuccess }
        );
      } else {
        sendBulkWhatsApp(
          {
            customer_identification,
            entity_identification: user.entity_identification,
            group_identification: identification,
            data: payload,
          },
          { onSuccess }
        );
      }
    } else {
      sendWhatsApp(
        {
          customer_identification,
          entity_identification: user.entity_identification,
          user_identification: identification,
          data: payload,
        },
        { onSuccess: () => onClose() }
      );
    }
  };

  const scheduleTooltip = schedule.isIncomplete
    ? (t(
        "dashboard.entities.viewEntityUser.sendMsg.scheduleIncompleteHint"
      ) as string)
    : undefined;

  const notConfigured = !isConfigLoading && config && !configured;

  const configLoadFailed = !isConfigLoading && isConfigError && !config;

  // Design system forbids showing Meta's raw enums (TIER_1K, GREEN); drop unknown values.
  const qualityLabel = senderQualityLabel(config?.sender?.quality, t);

  const rowLanguage = (tpl: SendableTemplate): string =>
    tpl.languages.includes(pickerLanguage) ? pickerLanguage : tpl.languages[0];

  const headlineFor = (tpl: SendableTemplate): string => {
    const lang = rowLanguage(tpl);
    const body =
      tpl.kind === "custom" ? tpl.body ?? "" : tpl.bodyByLang?.[lang] ?? "";
    return headlineOf(body, tpl.samplesByLang?.[lang], sharedOpenings[lang]);
  };

  const chooseTemplate = (tpl: SendableTemplate) => {
    setValue("template_key", tpl.value, { shouldValidate: true });
    setValue("language", rowLanguage(tpl), { shouldValidate: true });
    setPickerMode("fill");
  };

  const toggleFavorite = (tpl: SendableTemplate) => {
    if (pendingFavorite) return;
    setPendingFavorite(tpl.value);
    setWhatsappFavorite(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        data: { template: tpl.value, favorite: !tpl.favorite },
      },
      { onSettled: () => setPendingFavorite(null) }
    );
  };

  // Two states, never both: the saved line in plain words, or the controls.
  const renderVariableRow = (row: { key: string; label: string }) => {
    const binding = bindings[row.key];
    const savedEntry = savedMapping[row.key];

    if (isRowCollapsed(row.key) && savedEntry) {
      return (
        <div key={row.key} className="flex items-start gap-3">
          <span className="w-32 shrink-0 text-xs leading-tight text-gray-500">
            {row.label}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
            <NoTranslate>{entryValueLabel(savedEntry)}</NoTranslate>
          </span>
          <span className="shrink-0 text-xs text-gray-400">
            {entryOriginLabel(savedEntry)}
          </span>
          <button
            type="button"
            onClick={() => setExpandedRows((rows) => [...rows, row.key])}
            aria-label={t(wk("editValue")) as string}
            className="shrink-0 rounded p-1 text-gray-400 hover:text-blue-600"
          >
            <FaPen className="h-3 w-3" />
          </button>
        </div>
      );
    }

    return (
      <div key={row.key} className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium text-gray-700">{row.label}</span>
          {savedEntry && !rowMatchesSaved(row.key) && (
            <span className="text-xs text-gray-500">
              {t(wk("mappingChanged"))}
            </span>
          )}
        </div>
        <fieldset className="space-y-2">
          <legend className="mb-1 text-xs text-gray-500">
            {t(wk("varQuestion"))}
          </legend>

          <div className="space-y-2 pl-5">
            {(["text", "field", "data"] as const).map((kind) => (
              <div key={kind}>
                <label className="flex cursor-pointer items-baseline gap-2">
                  <input
                    type="radio"
                    name={`wa-kind-${row.key}`}
                    className="h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={rowKind(row.key) === kind}
                    onChange={() => setRowKind(row.key, kind)}
                  />
                  <span className="text-sm text-gray-800">
                    {t(wk(`varKind_${kind}`))}
                  </span>
                  <span className="min-w-0 flex-1 text-xs text-gray-500">
                    {t(wk(`varKindHint_${kind}`))}
                  </span>
                </label>

                {rowKind(row.key) === kind && kind === "text" && (
                  <input
                    type="text"
                    className={twMerge(fieldClass, "mt-1")}
                    value={variables[row.key] ?? ""}
                    onChange={(e) =>
                      setVariables((prev) => ({
                        ...prev,
                        [row.key]: e.target.value,
                      }))
                    }
                  />
                )}

                {rowKind(row.key) === kind && kind === "field" && binding && (
                  <div className="mt-1 space-y-1">
                    <select
                      className={fieldClass}
                      value={binding.value}
                      onChange={(e) => setRowMode(row.key, e.target.value)}
                    >
                      {NATIVE_FIELDS.map((field) => (
                        <option key={field} value={field}>
                          {t(wk(`field_${field}`))}
                        </option>
                      ))}
                    </select>
                    <span className="block text-xs text-gray-500">
                      {nativeExample(binding.value)}
                    </span>
                  </div>
                )}

                {rowKind(row.key) === kind && kind === "data" && binding && (
                  <div className="mt-1 space-y-1">
                    <input
                      type="text"
                      className={fieldClass}
                      value={binding.value}
                      onChange={(e) => updatePath(row.key, e.target.value)}
                      placeholder={t(wk("pathPlaceholder")) as string}
                    />
                    {binding.value.trim().length === 0 ? (
                      <span className="block text-xs text-gray-500">
                        {t(wk("pathHint"))}
                      </span>
                    ) : checkedPath(row.key)?.status === "found" ? (
                      <span className="block text-xs text-gray-500">
                        <NoTranslate>
                          {(checkedPath(row.key) as { value: string }).value}
                        </NoTranslate>
                      </span>
                    ) : checkedPath(row.key)?.status === "missing" ? (
                      <span className="block text-xs text-red-600">
                        {t(wk("pathMissing"))}
                      </span>
                    ) : checkedPath(row.key)?.status === "error" ? (
                      <span className="block text-xs text-red-600">
                        {t(wk("pathCheckFailed"))}
                      </span>
                    ) : (
                      <span className="block text-xs text-gray-500">
                        {t(wk("pathNotChecked"))}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}

            {binding?.source === "field" &&
              !isBulkMessage &&
              !(recipientFields?.[binding.value] ?? "").trim() && (
                <p className="text-xs text-red-600">{t(wk("pathMissing"))}</p>
              )}

            {binding && isBulkMessage && coverageLabel(row.key) && (
              <p className="text-xs text-gray-500">{coverageLabel(row.key)}</p>
            )}
          </div>

          {binding && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700">
                {fallbackLabel(row.key)}:
              </span>
              <input
                type="text"
                className={fieldClass}
                value={binding.fallback}
                onChange={(e) =>
                  updateBinding(row.key, { fallback: e.target.value })
                }
                placeholder={t(wk("fallbackPlaceholder")) as string}
              />
            </label>
          )}
        </fieldset>
      </div>
    );
  };

  const renderTemplateRow = (tpl: SendableTemplate) => {
    const selected = templateKey === tpl.value;
    const blockedForGroup = Boolean(isBulkMessage && tpl.isPayment);
    const wk = (suffix: string) =>
      `dashboard.entities.viewEntityUser.sendWhatsApp.${suffix}`;
    return (
      <li key={tpl.value} className={twMerge("pr-2", selected && "bg-blue-50")}>
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => chooseTemplate(tpl)}
            disabled={blockedForGroup}
            aria-current={selected ? "true" : undefined}
            className={twMerge(
              "flex min-w-0 flex-1 items-center gap-2 px-3 py-3 text-left",
              blockedForGroup && "cursor-not-allowed opacity-60",
              !selected && !blockedForGroup && "hover:bg-gray-50"
            )}
          >
            <span
              aria-hidden="true"
              className={twMerge(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                selected ? "border-blue-600" : "border-gray-300"
              )}
            >
              {selected && (
                <span className="h-2 w-2 rounded-full bg-blue-600" />
              )}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
              <NoTranslate>{headlineFor(tpl)}</NoTranslate>
            </span>
            {tpl.hasMedia && (
              <FiImage
                className="h-3.5 w-3.5 shrink-0 text-gray-400"
                title={t(wk("imageBadge")) as string}
              />
            )}
            {tpl.languages.length === 1 && (
              <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs uppercase text-gray-500">
                {rowLanguage(tpl)}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => toggleFavorite(tpl)}
            disabled={pendingFavorite !== null}
            aria-label={
              t(wk(tpl.favorite ? "removeFavorite" : "addFavorite")) as string
            }
            className="shrink-0 rounded p-1 text-gray-300 hover:text-blue-600 disabled:opacity-50"
          >
            {tpl.favorite ? (
              <FaStar className="h-3.5 w-3.5 text-blue-600" />
            ) : (
              <FaRegStar className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
        {blockedForGroup && (
          <p className="px-3 pb-2 text-xs text-gray-500">
            {t(wk("paymentNotForGroup"))}
          </p>
        )}
      </li>
    );
  };

  return (
    <>
      <ModalShell
        show={show}
        onClose={onClose}
        size="xl"
        title={t("dashboard.entities.viewEntityUser.sendWhatsApp.title")}
        subtitle={
          userName ? (
            <NoTranslate>
              {users &&
                users.length > 0 &&
                t(
                  "dashboard.entities.viewEntityUser.sendWhatsApp.selectedUsersOf"
                )}
              {userName}
              {userPhone && (
                <span className="text-gray-400"> ({userPhone})</span>
              )}
            </NoTranslate>
          ) : undefined
        }
        onSubmit={
          notConfigured || configLoadFailed
            ? undefined
            : handleSubmit(performSend)
        }
        footer={
          configLoadFailed ? (
            <div className="flex flex-wrap items-center justify-end gap-3">
              <CustomButton
                variant="secondary"
                onClick={onClose}
                title={t("common.cancel") as string}
              />
            </div>
          ) : notConfigured ? (
            <div className="flex flex-wrap items-center justify-end gap-3">
              <CustomButton
                variant="secondary"
                onClick={onClose}
                title={t("common.cancel") as string}
              />
              {senderPending ||
              senderFailed ||
              !permissions.isOwner() ? null : (
                <CustomButton
                  variant="primary"
                  onClick={openWhatsappSetupRequest}
                  title={
                    t(
                      "dashboard.entities.viewEntityUser.sendWhatsApp.setupBySupportCta"
                    ) as string
                  }
                />
              )}
            </div>
          ) : (
            <div className="flex w-full flex-col gap-3">
              {schedule.armed && (
                <ScheduleControl
                  id="whatsapp-schedule-panel"
                  schedule={schedule}
                  control={control}
                  valueName="scheduledDateTime"
                  error={errors.scheduledDateTime}
                />
              )}
              <div className="flex flex-wrap items-center justify-end gap-3">
                <CustomButton
                  variant="secondary"
                  onClick={onClose}
                  disabled={isSending}
                  title={t("common.cancel") as string}
                />
                <div className="flex items-stretch">
                  <CustomButton
                    type="submit"
                    variant="primary"
                    isLoading={isSending}
                    disabled={isSending || !canSend}
                    className="rounded-r-none"
                    tooltip={scheduleTooltip}
                    comp={
                      schedule.armed ? (
                        <div className="flex items-center gap-1 sm:gap-2">
                          <BiCalendar size={16} className="text-white" />
                          <span>
                            {t(
                              "dashboard.entities.viewEntityUser.sendWhatsApp.schedule"
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 sm:gap-2">
                          <PiTelegramLogo size={16} className="text-white" />
                          <span>
                            {t(
                              "dashboard.entities.viewEntityUser.sendWhatsApp.send"
                            )}
                          </span>
                        </div>
                      )
                    }
                  />
                  <CustomButton
                    type="button"
                    variant="primary"
                    onClick={schedule.toggle}
                    disabled={isSending}
                    className="rounded-l-none border-l border-blue-500 px-2"
                    aria-expanded={schedule.armed}
                    aria-controls="whatsapp-schedule-panel"
                    aria-label={
                      t(
                        "dashboard.entities.viewEntityUser.sendMsg.buttons.scheduleOption"
                      ) as string
                    }
                    comp={
                      <FaChevronDown
                        size={14}
                        className={twMerge(
                          "text-white transition-transform",
                          schedule.armed && "rotate-180"
                        )}
                      />
                    }
                  />
                </div>
              </div>
            </div>
          )
        }
      >
        {isConfigLoading ? (
          <p className="text-sm text-gray-500">
            {t("dashboard.entities.viewEntityUser.sendWhatsApp.loading")}
          </p>
        ) : configLoadFailed ? (
          <div className="space-y-3">
            <AlertMessage color="failure">
              {t(wk("configLoadFailed"))}
            </AlertMessage>
            <CustomButton
              variant="secondary"
              onClick={() => refetchConfig()}
              isLoading={isConfigFetching}
              disabled={isConfigFetching}
              title={t("common.retry") as string}
            />
          </div>
        ) : notConfigured ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-800">
              <FaWhatsapp size={20} className="text-green-600" />
              <span className="text-sm font-semibold">
                {t(
                  "dashboard.entities.viewEntityUser.sendWhatsApp.notConfiguredTitle"
                )}
              </span>
            </div>
            {availableCredits > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3">
                <PiCoinsLight className="h-4 w-4 flex-shrink-0 text-blue-600" />
                <p className="text-xs text-blue-800">
                  {t(
                    "dashboard.entities.viewEntityUser.sendWhatsApp.creditsAvailable",
                    {
                      credits: availableCredits,
                    }
                  )}
                </p>
              </div>
            )}
            {senderFailed ? (
              <p className="text-sm text-gray-600">
                {t(
                  senderWasOnline
                    ? "dashboard.entities.viewEntityUser.sendWhatsApp.senderStopped"
                    : "dashboard.entities.viewEntityUser.sendWhatsApp.activationFailed"
                )}{" "}
                <button
                  type="button"
                  onClick={openLargeEnterpriseSupport}
                  className="font-medium text-blue-600 underline"
                >
                  {t(
                    "dashboard.entities.viewEntityUser.sendWhatsApp.contactSupport"
                  )}
                </button>
              </p>
            ) : senderPending ? (
              <p className="text-sm text-gray-600">
                {t(
                  "dashboard.entities.viewEntityUser.sendWhatsApp.activationPending"
                )}
              </p>
            ) : !permissions.isOwner() ? (
              <p className="text-sm text-gray-600">
                {t(
                  "dashboard.entities.viewEntityUser.sendWhatsApp.activationOwnerOnly"
                )}
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  {t(
                    "dashboard.entities.viewEntityUser.sendWhatsApp.setupBySupportExplanation"
                  )}
                </p>
                <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-600">
                  <li>
                    {t(
                      "dashboard.entities.viewEntityUser.sendWhatsApp.setupBySupportStep1"
                    )}
                  </li>
                  <li>
                    {t(
                      "dashboard.entities.viewEntityUser.sendWhatsApp.setupBySupportStep2"
                    )}
                  </li>
                  <li>
                    {t(
                      "dashboard.entities.viewEntityUser.sendWhatsApp.setupBySupportStep3"
                    )}
                  </li>
                </ol>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {config?.sender?.from && (
              <p className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                {t(
                  "dashboard.entities.viewEntityUser.sendWhatsApp.senderNumberLine"
                )}
                <NoTranslate>
                  <span className="font-medium text-gray-700">
                    {config.sender.from}
                  </span>
                </NoTranslate>
                {qualityLabel && <span>{qualityLabel}</span>}
              </p>
            )}
            {isWhatsappBlocked && (
              <div className="rounded-lg bg-red-50 p-3 border border-red-200">
                <p className="text-sm text-red-800 font-medium">
                  {t(
                    "dashboard.entities.viewEntityUser.sendWhatsApp.communicationBlocked"
                  )}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {t(
                    "dashboard.entities.viewEntityUser.sendWhatsApp.templateLabel"
                  )}
                </span>
                {permissions.isOwner() && (
                  <CustomButton
                    variant="secondary"
                    size="xs"
                    onClick={() => setShowManageTemplates(true)}
                    title={
                      t(
                        "dashboard.entities.viewEntityUser.sendWhatsApp.manageTemplatesCta"
                      ) as string
                    }
                  />
                )}
              </div>

              {templates.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-xs text-gray-500">
                  {t(
                    "dashboard.entities.viewEntityUser.sendWhatsApp.noApprovedTemplate"
                  )}
                </p>
              ) : pickerMode === "fill" && selectedTemplate ? (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-600 bg-blue-50 p-2.5">
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
                    <NoTranslate>{headlineFor(selectedTemplate)}</NoTranslate>
                  </span>
                  <CustomButton
                    variant="secondary"
                    size="xs"
                    onClick={() => setPickerMode("choose")}
                    title={
                      t(
                        "dashboard.entities.viewEntityUser.sendWhatsApp.changeTemplate"
                      ) as string
                    }
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {favoriteTemplates.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                        {t(
                          "dashboard.entities.viewEntityUser.sendWhatsApp.favoritesGroup"
                        )}
                      </span>
                      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                        {favoriteTemplates.map(renderTemplateRow)}
                      </ul>
                    </div>
                  )}

                  {ownTemplates.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                        {t(
                          "dashboard.entities.viewEntityUser.sendWhatsApp.customGroup"
                        )}
                      </span>
                      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                        {ownTemplates.map(renderTemplateRow)}
                      </ul>
                    </div>
                  )}

                  {libraryTemplates.length > 0 && (
                    <div>
                      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          {t(
                            "dashboard.entities.viewEntityUser.sendWhatsApp.libraryGroup"
                          )}
                        </span>
                        {/* Not a button: a tab-like switch over what the rows say. */}
                        {libraryLanguages.length > 1 && (
                          <div className="flex items-center gap-1">
                            {libraryLanguages.map((lang) => (
                              <button
                                key={lang}
                                type="button"
                                onClick={() => setPickerLanguage(lang)}
                                className={twMerge(
                                  "rounded-md px-2 py-0.5 text-xs uppercase",
                                  pickerLanguage === lang
                                    ? "bg-blue-50 text-blue-700"
                                    : "text-gray-500 hover:bg-gray-100"
                                )}
                              >
                                {lang}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
                        {groupedLibrary.length === 0 && (
                          <p className="px-3 py-3 text-xs text-gray-500">
                            {t(
                              "dashboard.entities.viewEntityUser.sendWhatsApp.libraryAllFavorited"
                            )}
                          </p>
                        )}
                        {groupedLibrary.map((group) => (
                          <div key={group.id}>
                            <div className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                              {t(
                                `dashboard.entities.viewEntityUser.sendWhatsApp.templateGroups.${group.id}`
                              )}
                            </div>
                            <ul className="divide-y divide-gray-100">
                              {group.rows.map(renderTemplateRow)}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {pickerMode === "fill" && (
              <>
                {isPaymentTemplate && !isBulkMessage && (
                  <div>
                    <span className="mb-1 block text-sm font-medium text-gray-700">
                      {t(wk("paymentLinkLabel"))}
                    </span>
                    {isLoadingPaymentLinks ? (
                      <p className="text-xs text-gray-500">
                        {t(wk("loading"))}
                      </p>
                    ) : (paymentLinks ?? []).length === 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">
                          {t(wk("paymentLinkNone"))}
                        </p>
                        <CustomButton
                          variant="secondary"
                          size="xs"
                          onClick={() => setShowCreatePaymentLink(true)}
                          title={t(wk("paymentLinkCreate")) as string}
                        />
                      </div>
                    ) : (
                      <select
                        aria-label={t(wk("paymentLinkLabel")) as string}
                        className={fieldClass}
                        value={paymentLinkValue}
                        onChange={(event) => {
                          if (event.target.value === CREATE_PAYMENT_LINK) {
                            setShowCreatePaymentLink(true);
                            return;
                          }
                          setVariables((prev) => ({
                            ...prev,
                            [String(paymentLinkIndex)]: event.target.value,
                          }));
                        }}
                      >
                        <option value="">
                          {t(wk("paymentLinkPlaceholder"))}
                        </option>
                        <option value={CREATE_PAYMENT_LINK}>
                          {t(wk("paymentLinkCreate"))}
                        </option>
                        {(paymentLinks ?? []).map((link: any) => (
                          <option
                            key={link.payment_unique_link}
                            value={link.payment_unique_link}
                          >
                            {link.description ||
                              t(wk("paymentLinkNoDescription"))}
                            {" · "}
                            {formatAmount(link.amount, link.currency)}
                            {link.recurrency && link.recurrency !== "UNIQUE"
                              ? ` · ${t(wk("paymentLinkRecurring"))}`
                              : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {variableRows.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-1">
                      <span className="text-sm font-medium text-gray-700">
                        {t(
                          "dashboard.entities.viewEntityUser.sendWhatsApp.variablesTitle"
                        )}
                      </span>
                      {mappingSavable ? (
                        <CustomButton
                          variant="secondary"
                          size="xs"
                          onClick={handleSaveMapping}
                          isLoading={isSavingMapping}
                          title={
                            t(
                              "dashboard.entities.viewEntityUser.sendWhatsApp.mappingSave"
                            ) as string
                          }
                        />
                      ) : hasSavedMapping ? (
                        <span className="text-xs text-gray-500">
                          {t(
                            "dashboard.entities.viewEntityUser.sendWhatsApp.mappingSaved"
                          )}
                        </span>
                      ) : null}
                    </div>

                    {!hasSavedMapping && (
                      <p className="text-xs text-gray-500">
                        {t(
                          "dashboard.entities.viewEntityUser.sendWhatsApp.mappingEmptyHint"
                        )}
                      </p>
                    )}

                    {variableRows.map(renderVariableRow)}

                    {selectedTemplate?.automatable && !mappingDiffers && (
                      <p className="text-xs text-gray-500">
                        {t(
                          "dashboard.entities.viewEntityUser.sendWhatsApp.mappingAllFilled"
                        )}
                      </p>
                    )}
                  </div>
                )}

                {selectedTemplate?.hasMedia && (
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-gray-700">
                      {t(
                        "dashboard.entities.viewEntityUser.sendWhatsApp.mediaLabel"
                      )}
                    </span>
                    <WhatsAppImageUpload
                      value={mediaUrl ?? ""}
                      onUploaded={(url) =>
                        setValue("media_url", url, { shouldValidate: true })
                      }
                      onRemoved={() =>
                        setValue("media_url", "", { shouldValidate: true })
                      }
                    />
                    {mediaError && (
                      <span className="mt-1 block text-xs text-red-600">
                        {mediaError}
                      </span>
                    )}
                  </label>
                )}

                {selectedTemplate && previewBody && (
                  <div>
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-700">
                        {t(
                          "dashboard.entities.viewEntityUser.sendWhatsApp.previewLabel"
                        )}
                      </span>
                      {!isBulkMessage && hasPathRow && (
                        <CustomButton
                          variant="secondary"
                          size="xs"
                          onClick={refreshPreview}
                          isLoading={isCheckingPaths}
                          title={
                            t(
                              "dashboard.entities.viewEntityUser.sendWhatsApp.refreshPreview"
                            ) as string
                          }
                        />
                      )}
                    </div>
                    <NoTranslate>
                      <WhatsAppMessagePreview
                        imageUrl={
                          selectedTemplate.hasMedia
                            ? (mediaUrl ?? "").trim() || null
                            : null
                        }
                        body={previewBody}
                        isMarketing={selectedTemplate.category === "marketing"}
                        buttons={previewButtons}
                      />
                    </NoTranslate>
                  </div>
                )}
              </>
            )}

            {pickerMode === "fill" && fallbackWarnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-800">
                  {t(wk("fallbackWarningTitle"), {
                    count: coverageTotal ?? bulkRecipientCount,
                  })}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {fallbackWarnings.map((warning) => (
                    <li key={warning.key} className="text-xs text-amber-800">
                      {t(
                        wk(
                          warning.approximate
                            ? "fallbackWarningRowApprox"
                            : "fallbackWarningRow"
                        ),
                        {
                          count: warning.count,
                          fallback: warning.fallback,
                          source: entryValueLabel(warning.entry),
                        }
                      )}
                      {warning.unknownToAll && (
                        <span className="block font-medium">
                          {t(wk("fallbackWarningUnknownPath"))}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {hasEnoughCredits ? (
              <WhatsappCreditWarning
                whatsappCredit={availableCredits}
                configured={configured}
                onAddCredit={onAddCredit}
              />
            ) : (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-1 items-center gap-2">
                    <PiCoinsLight className="h-4 w-4 flex-shrink-0 text-red-600" />
                    <p className="text-xs text-red-800">
                      {isBulkMessage && bulkRecipientCount > 0
                        ? t(
                            "dashboard.entities.viewEntityUser.sendWhatsApp.notEnoughCreditsForRecipients",
                            {
                              credits: t(
                                "dashboard.entities.viewEntityUser.sendWhatsApp.creditCount",
                                { count: availableCredits }
                              ),
                              count: bulkRecipientCount,
                            }
                          )
                        : t(
                            "dashboard.entities.viewEntityUser.sendWhatsApp.creditsAvailable",
                            { credits: availableCredits }
                          )}
                    </p>
                  </div>
                  <CustomButton
                    variant="outline"
                    onClick={onAddCredit}
                    title={
                      t(
                        "dashboard.entities.viewEntityUser.sendWhatsApp.addCredit"
                      ) as string
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </ModalShell>
      <CreatePaymentRequestModal
        show={showCreatePaymentLink}
        onClose={() => setShowCreatePaymentLink(false)}
        userIdentification={identification}
        onSuccess={(created: any) => {
          setShowCreatePaymentLink(false);
          invalidateUserPaymentLinks(
            user.entity_identification,
            identification
          );
          if (paymentLinkIndex != null && created?.payment_unique_link) {
            setVariables((prev) => ({
              ...prev,
              [String(paymentLinkIndex)]: created.payment_unique_link,
            }));
          }
        }}
      />
      {permissions.isOwner() && (
        <ManageWhatsAppTemplatesModal
          show={showManageTemplates}
          onClose={() => setShowManageTemplates(false)}
        />
      )}
    </>
  );
};

export default SendWhatsAppModal;
